import axios from 'axios';
import { PUSH_PUBLIC_KEY, PUSH_SUBSCRIBE_PATH, PUSH_UNSUBSCRIBE_PATH } from '../config/push';

function withTimeout(promise, message, timeoutMs = 10000) {
  let timeoutId;

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error(message));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    window.clearTimeout(timeoutId);
  });
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function getPushDiagnostics() {
  let existingRegistration = null;

  try {
    if ('serviceWorker' in navigator) {
      existingRegistration = await navigator.serviceWorker.getRegistration();
    }
  } catch {
    existingRegistration = null;
  }

  return {
    origin: window.location.origin,
    isSecureContext: window.isSecureContext,
    hasServiceWorkerApi: 'serviceWorker' in navigator,
    hasPushManagerApi: 'PushManager' in window,
    hasNotificationApi: 'Notification' in window,
    notificationPermission: 'Notification' in window ? Notification.permission : 'unsupported',
    serviceWorkerController: !!navigator.serviceWorker?.controller,
    serviceWorkerRegistered: !!existingRegistration,
    serviceWorkerActive: !!existingRegistration?.active,
  };
}

export async function getServiceWorkerRegistration() {
  if (!isPushSupported()) {
    throw new Error('Push notifications nu sunt suportate pe acest dispozitiv/browser.');
  }

  let existingRegistration = await navigator.serviceWorker.getRegistration();
  if (existingRegistration?.active) {
    return existingRegistration;
  }

  const readyRegistration = await withTimeout(
    navigator.serviceWorker.ready,
    'Service worker-ul nu este gata. Reincarca aplicatia, deschide din nou aplicatia si incearca din nou.',
    12000
  );

  if (!readyRegistration?.active) {
    throw new Error('Service worker-ul nu este activ.');
  }

  return readyRegistration;
}

export async function getCurrentPushSubscription() {
  const registration = await getServiceWorkerRegistration();
  return registration.pushManager.getSubscription();
}

export async function subscribeToPushNotifications() {
  if (!PUSH_PUBLIC_KEY) {
    throw new Error('Lipseste VUE_APP_PUSH_PUBLIC_KEY.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permisiunea pentru notificari nu a fost acordata.');
  }

  const registration = await getServiceWorkerRegistration();
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    return existingSubscription;
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(PUSH_PUBLIC_KEY),
  });
}

export async function savePushSubscription(subscription, extra = {}) {
  await withTimeout(
    axios.post(PUSH_SUBSCRIBE_PATH, {
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      keys: subscription.toJSON().keys,
      ...extra,
    }, { timeout: 10000 }),
    'Serverul nu a raspuns la salvarea notificarii push.'
  );
}

export async function deletePushSubscription(subscription, extra = {}) {
  await withTimeout(
    axios.post(PUSH_UNSUBSCRIBE_PATH, {
      endpoint: subscription.endpoint,
      expirationTime: subscription.expirationTime,
      keys: subscription.toJSON().keys,
      ...extra,
    }, { timeout: 10000 }),
    'Serverul nu a raspuns la stergerea notificarii push.'
  );
}

export async function unsubscribeFromPushNotifications() {
  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return false;
  }

  return withTimeout(
    subscription.unsubscribe(),
    'Nu am putut dezactiva notificarile push.'
  );
}
